import { Component, ElementRef, OnInit, signal, effect, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { PlatformHeader } from '../platform-header/platform-header.component';
import { PlatformSidebar } from '../platform-sidebar/platform-sidebar.component';
import { LogoutModalComponent } from '../../../shared/components/logout-modal/logout-modal.component';
import { PlatformConfigService } from '../../services/platform-config.service';
import { AuthService } from '../../services/auth/auth.service';
import { NavItem, PlatformConfig, PlatformType } from '../../models/platform.model';
import { filter } from 'rxjs';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../../store/auth/auth.actions';
import { APP_PATHS } from '@core/constants/platform-config.constants';
import { LayoutEventService } from '@core/services/layout-event.service';

@Component({
  selector: 'app-platform-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    PlatformHeader,
    PlatformSidebar,
    LogoutModalComponent,
  ],
  templateUrl: './platform-layout.component.html',
  styleUrl: './platform-layout.component.css',
})
export class PlatformLayout implements OnInit {
  userPlatforms: PlatformConfig[] = [];
  currentMenuItems = signal<NavItem[]>([]);
  sidebarVisible = signal<boolean>(true);
  sidebarPinned = signal<boolean>(true);
  logoutModalVisible = signal<boolean>(false);
  isCheckInPage = signal<boolean>(false);

  private readonly contentScrollArea = viewChild<ElementRef<HTMLElement>>('contentScroll');
  private readonly layoutEventService = inject(LayoutEventService);

  constructor(
    public platformConfigService: PlatformConfigService,
    private authService: AuthService,
    private router: Router,
    private store: Store
  ) {
    effect(() => {
      this.loadMenuItems();
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.isCheckInPage.set(
        this.router.url.includes(`/${APP_PATHS.CHECK_IN_AND_JOB_CARDS}/${APP_PATHS.MAINTENANCE}`)
      );
      this.scrollContentToTop();
    });
  }

  ngOnInit() {
    this.userPlatforms = this.platformConfigService.getUserAccessiblePlatforms();
    this.isCheckInPage.set(
      this.router.url.includes(`/${APP_PATHS.CHECK_IN_AND_JOB_CARDS}/${APP_PATHS.MAINTENANCE}`)
    );
  }

  onPlatformChange(platformId: PlatformType): void {
    const config = this.platformConfigService.getPlatformConfig(platformId);
    const route = this.platformConfigService.resolveDefaultRoute(config);
    if (route) {
      this.router.navigate([route]);
    }
  }

  onLogout(): void {
    this.logoutModalVisible.set(true);
  }

  confirmLogout(): void {
    this.logoutModalVisible.set(false);
    this.store.dispatch(AuthActions.logout());
  }

  cancelLogout(): void {
    this.logoutModalVisible.set(false);
  }

  toggleSidebar(): void {
    this.sidebarVisible.update((visible) => !visible);
    if (this.sidebarVisible()) {
      this.layoutEventService.sidebarOpened$.next();
    }
  }

  closeSidebar(): void {
    this.sidebarVisible.set(false);
  }

  togglePin(): void {
    this.sidebarPinned.update((pinned) => !pinned);
    if (this.sidebarPinned()) {
      // When pinning, ensure sidebar is visible
      this.sidebarVisible.set(true);
    } else {
      // When unpinning, close the sidebar
      this.sidebarVisible.set(false);
    }
  }

  private scrollContentToTop(): void {
    queueMicrotask(() => {
      const el = this.contentScrollArea()?.nativeElement;
      el?.scrollTo({ top: 0, left: 0 });
    });
  }

  private loadMenuItems(): void {
    const config = this.platformConfigService.currentConfig();
    const visibleItems = (config?.navItems || [])
      .filter((item) => !item.hideInSidebar)
      .map((item) => ({
        ...item,
        children: item.children ? item.children.filter((child) => !child.hideInSidebar) : undefined,
      }));
    this.currentMenuItems.set(visibleItems);
  }
}
